import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from '@app/layout/AppLayout';
import { isOwnerLicense, useLicense } from '@shared/license';
import { useActiveMission } from '@shared/state';
import { LicenseGate } from './LicenseGate';
import { CraPreviewDemo, DevisPreviewDemo, MissioPreviewDemo, RunwayPreviewDemo, TresoreriePreviewDemo } from './tool-previews';

// Tab pages
const AccueilPage = lazy(() => import('@app/pages/AccueilPage').then((m) => ({ default: m.AccueilPage })));
const MissionsPage = lazy(() => import('@app/pages/MissionsPage').then((m) => ({ default: m.MissionsPage })));
const MissionDetailPage = lazy(() => import('@modules/mission-detail/pages/MissionDetailPage').then((m) => ({ default: m.MissionDetailPage })));
const FinancePage = lazy(() => import('@app/pages/FinancePage').then((m) => ({ default: m.FinancePage })));
const DocumentsPage = lazy(() => import('@app/pages/DocumentsPage').then((m) => ({ default: m.DocumentsPage })));
const MoiPage = lazy(() => import('@app/pages/MoiPage').then((m) => ({ default: m.MoiPage })));

// Tool pages
const CraPage = lazy(() => import('@modules/cra/pages/CraPage').then((module) => ({ default: module.CraPage })));
const CalcMissionPage = lazy(() => import('@modules/calc-mission/pages/CalcMissionPage').then((module) => ({ default: module.CalcMissionPage })));
const ScratchpadPage = lazy(() => import('@modules/calc-mission/pages/ScratchpadPage').then((module) => ({ default: module.ScratchpadPage })));
const GoalPage = lazy(() => import('@modules/calc-mission/pages/GoalPage').then((module) => ({ default: module.GoalPage })));
const ProjectPage = lazy(() => import('@modules/calc-mission/pages/ProjectPage').then((module) => ({ default: module.ProjectPage })));
const CalcMissionSharePage = lazy(() => import('@modules/calc-mission/pages/CalcMissionSharePage').then((module) => ({ default: module.CalcMissionSharePage })));
const ChronoSharePage = lazy(() => import('@modules/chrono/pages/ChronoSharePage').then((module) => ({ default: module.ChronoSharePage })));
const ChronoPage = lazy(() => import('@modules/chrono/pages/ChronoPage').then((module) => ({ default: module.ChronoPage })));
const DealRoomPage = lazy(() => import('@modules/deal-room/pages/DealRoomPage').then((module) => ({ default: module.DealRoomPage })));
const DealRoomSharePage = lazy(() => import('@modules/deal-room/pages/DealRoomSharePage').then((module) => ({ default: module.DealRoomSharePage })));
const DevisPage = lazy(() => import('@modules/devis/pages/DevisPage').then((module) => ({ default: module.DevisPage })));
const DevisSharePage = lazy(() => import('@modules/devis/pages/DevisSharePage').then((module) => ({ default: module.DevisSharePage })));
const MissionEvalPage = lazy(() => import('@modules/mission-eval/pages/MissionEvalPage').then((module) => ({ default: module.MissionEvalPage })));
const MissioPage = lazy(() => import('@modules/missio/pages/MissioPage').then((module) => ({ default: module.MissioPage })));
const MissioSharePage = lazy(() => import('@modules/missio/pages/MissioSharePage').then((module) => ({ default: module.MissioSharePage })));
const MissionCanvasPage = lazy(() => import('@modules/mission-canvas').then((module) => ({ default: module.MissionCanvasPage })));
const SipocPage = lazy(() => import('@modules/sipoc/pages/SipocPage').then((module) => ({ default: module.SipocPage })));
const MoscowPage = lazy(() => import('@modules/moscow/pages/MoscowPage').then((module) => ({ default: module.MoscowPage })));
const ProfilePage = lazy(() => import('@modules/profile/pages/ProfilePage').then((module) => ({ default: module.ProfilePage })));
const RolesPage = lazy(() => import('@modules/raci/pages/RolesPage').then((module) => ({ default: module.RolesPage })));
const RaciPage = lazy(() => import('@modules/raci/pages/RaciPage').then((module) => ({ default: module.RaciPage })));
const RunwayPage = lazy(() => import('@modules/runway/pages/RunwayPage').then((module) => ({ default: module.RunwayPage })));
const TreasuryPage = lazy(() => import('@modules/tresorerie/pages/TreasuryPage').then((module) => ({ default: module.TreasuryPage })));
const ActivationPage = lazy(() => import('@modules/activation/pages/ActivationPage').then((module) => ({ default: module.ActivationPage })));
const EstimationPage = lazy(() => import('@modules/estimation/pages/EstimationPage').then((module) => ({ default: module.EstimationPage })));
const NotesPage = lazy(() => import('@modules/notes/pages/NotesPage').then((module) => ({ default: module.NotesPage })));
const ProjectionsPage = lazy(() => import('@modules/projections/pages/ProjectionsPage').then((module) => ({ default: module.ProjectionsPage })));
const LegalPage = lazy(() => import('@app/pages/LegalPage').then((module) => ({ default: module.LegalPage })));
const ProposalPage = lazy(() => import('@modules/proposal/pages/ProposalPage').then((module) => ({ default: module.ProposalPage })));
const NewMissionWizard = lazy(() => import('@app/pages/NewMissionWizard').then((m) => ({ default: m.NewMissionWizard })));
const AssistantDocumentPage = lazy(() => import('@modules/assistant-document/pages/AssistantDocumentPage').then((module) => ({ default: module.AssistantDocumentPage })));
const TjmRadarPage = lazy(() => import('@modules/tjm-radar/pages/TjmRadarPage').then((module) => ({ default: module.TjmRadarPage })));
const MissionMemoryPage = lazy(() => import('@modules/mission-memory/pages/MissionMemoryPage').then((module) => ({ default: module.MissionMemoryPage })));
const FiscalPage = lazy(() => import('@modules/fiscal/pages/FiscalPage').then((module) => ({ default: module.FiscalPage })));
const BenchmarkPage = lazy(() => import('@modules/benchmark/pages/BenchmarkPage').then((module) => ({ default: module.BenchmarkPage })));
const ReportingPage = lazy(() => import('@modules/reporting/pages/ReportingPage').then((module) => ({ default: module.ReportingPage })));
const ClientRiskPage = lazy(() => import('@modules/client-risk/pages/ClientRiskPage').then((m) => ({ default: m.ClientRiskPage })));
const StackRegistryPage = lazy(() => import('@modules/stack-registry').then((m) => ({ default: m.StackRegistryPage })));
const AdminLicensesPage = lazy(() => import('@modules/admin/pages/AdminLicensesPage').then((m) => ({ default: m.AdminLicensesPage })));
const AdminUsagePage = lazy(() => import('@modules/admin/pages/AdminUsagePage').then((m) => ({ default: m.AdminUsagePage })));
const OnboardingPage = lazy(() => import('@app/pages/OnboardingPage').then((m) => ({ default: m.OnboardingPage })));

function RouteLoader() {
  return (
    <div className="card p-24 text-center">
      <p className="text-secondary text-14">Chargement…</p>
    </div>
  );
}

function withSuspense(node: JSX.Element) {
  return <Suspense fallback={<RouteLoader />}>{node}</Suspense>;
}

function OwnerOnlyAdminRoute() {
  const license = useLicense();
  if (!isOwnerLicense(license)) {
    return <Navigate to="/moi" replace />;
  }
  return withSuspense(<AdminLicensesPage />);
}

function OwnerOnlyUsageRoute() {
  const license = useLicense();
  if (!isOwnerLicense(license)) {
    return <Navigate to="/moi" replace />;
  }
  return withSuspense(<AdminUsagePage />);
}

/**
 * LOT 4 — compatibilité pour l'ancienne route `/projets` (ProjetsPage
 * supprimée, absorbée par l'écran canonique Missions). Un ancien
 * signet/deep-link continue de fonctionner : il atterrit sur le détail de
 * la mission active si elle existe, sinon sur la liste des missions.
 * Pas de double implémentation : simple redirection, aucune logique dupliquée.
 */
function ProjetsLegacyRedirect() {
  const activeMission = useActiveMission();
  return <Navigate to={activeMission ? `/missions/${activeMission.id}` : '/missions'} replace />;
}

/**
 * LOT 5.1 — `/missions/new` est désormais l'unique route canonique de
 * création (voir docs/HUB-LOT5.1-PARCOURS-UNIQUE.md). Les anciennes routes
 * `/new-mission-wizard` et `/new-mission` restent de simples redirections
 * de compatibilité pour ne pas casser un signet historique.
 */
function NewMissionLegacyRedirect() {
  return <Navigate to="/missions/new" replace />;
}

/**
 * LOT 6 — `/chiffrage` (AssistantChiffragePage, supprimée) exposait le même
 * moteur que CalcMissionPage (`computeCalcMissionSimpleResult`) via un
 * second chemin guidé. Absorbé dans la surface canonique "Chiffrer une
 * mission" ; l'ancienne route reste un lien de compatibilité (voir
 * docs/HUB-LOT6-CHIFFRAGE-TJM.md, Phase D/H).
 */
function ChiffrageLegacyRedirect() {
  return <Navigate to="/tools/calc-mission" replace />;
}

export function AppRouter() {
  return (
    <Routes>
      {/* Share pages (no layout) */}
      <Route path="share/resume-client" element={withSuspense(<DealRoomSharePage />)} />
      <Route path="share/calc-mission" element={withSuspense(<CalcMissionSharePage />)} />
      <Route path="share/missio" element={withSuspense(<MissioSharePage />)} />
      <Route path="share/devis" element={withSuspense(<DevisSharePage />)} />
      <Route path="share/chrono" element={withSuspense(<ChronoSharePage />)} />

      {/* Onboarding première utilisation (plein écran, sans shell) */}
      <Route path="onboarding" element={withSuspense(<OnboardingPage />)} />

      {/* Main app layout */}
      <Route path="/" element={<AppLayout />}>
        {/* Tab pages */}
        <Route index element={withSuspense(<AccueilPage />)} />
        <Route path="accueil" element={withSuspense(<AccueilPage />)} />
        <Route path="projets" element={<ProjetsLegacyRedirect />} />
        <Route path="missions" element={withSuspense(<MissionsPage />)} />
        <Route path="missions/new" element={withSuspense(<NewMissionWizard />)} />
        <Route path="missions/:id" element={withSuspense(<MissionDetailPage />)} />
        <Route path="opportunites" element={withSuspense(<MissioPage />)} />
        <Route path="documents" element={withSuspense(<DocumentsPage />)} />
        <Route path="new-mission" element={<NewMissionLegacyRedirect />} />
        <Route path="new-document" element={withSuspense(<AssistantDocumentPage />)} />
        <Route path="chiffrage" element={<ChiffrageLegacyRedirect />} />
        <Route path="new-mission-wizard" element={<NewMissionLegacyRedirect />} />
        <Route path="finance" element={withSuspense(<FinancePage />)} />
        <Route path="moi" element={withSuspense(<MoiPage />)} />
        <Route path="legal/confidentialite" element={withSuspense(<LegalPage kind="confidentialite" />)} />
        <Route path="legal/mentions-legales" element={withSuspense(<LegalPage kind="mentions-legales" />)} />
        <Route path="legal/cgv" element={withSuspense(<LegalPage kind="cgv" />)} />

        {/* Legacy redirect - dashboard goes to accueil */}
        <Route path="dashboard" element={withSuspense(<AccueilPage />)} />

        {/* Tool pages (still accessible) */}
        <Route path="tools/profile" element={withSuspense(<ProfilePage />)} />
        <Route path="tools/eval" element={withSuspense(<MissionEvalPage />)} />
        <Route
          path="tools/mission-canvas"
          element={withSuspense(
            <LicenseGate
              toolName="Cadrage mission"
              description="Cadrer une mission en 6 sections avant de dériver vers SIPOC, proposition et pilotage."
              preview={{
                eyebrow: 'Aperçu canvas',
                title: 'Cadrage mission',
                intro: 'La version Starter débloque le cadrage complet de la mission.',
                sampleLabel: 'Cadrage mission réunit pourquoi, objectif, parties prenantes, contraintes, risques et critères de succès.',
                items: [
                  { label: 'Sections', value: '6', note: 'Why, objectif, acteurs, contraintes, risques, succès.' },
                  { label: 'Score', value: '0-100', note: 'Verdict de flou à prêt.' },
                  { label: 'Auto-save', value: 'Oui', note: 'Sauvegarde locale continue.' },
                  { label: 'Handoffs', value: '3', note: 'SIPOC, MoSCoW, Proposition.' },
                ],
              }}
            >
              <MissionCanvasPage />
            </LicenseGate>,
          )}
        />
        <Route path="tools/sipoc" element={withSuspense(<SipocPage />)} />
        <Route path="tools/moscow" element={withSuspense(<MoscowPage />)} />
        <Route path="tools/roles" element={withSuspense(<RolesPage />)} />
        <Route path="tools/raci" element={withSuspense(<RaciPage />)} />
        <Route
          path="tools/deal-room"
          element={withSuspense(
            <LicenseGate toolName="Partage client" description="Présentez la mission au client avec un espace partagé." requiredTier="starter">
              <DealRoomPage />
            </LicenseGate>,
          )}
        />

        <Route
          path="tools/devis"
          element={withSuspense(
            <LicenseGate
              toolName="Devis"
              description="Préparez un devis depuis une mission, partagez-le puis revenez dans votre pilotage."
              preview={{
                eyebrow: 'Aperçu devis', title: 'Devis',
                intro: 'Préparez un devis, partagez-le.',
                demo: <DevisPreviewDemo />,
                items: [
                  { label: 'Brouillon', value: 'Oui', note: 'Depuis le projet.' },
                  { label: 'Partage', value: 'Oui', note: 'Vue client.' },
                  { label: 'Starter', value: 'Édition', note: 'Suivi et duplication.' },
                  { label: 'Pro', value: 'Complet', note: 'Pont trésorerie.' },
                ],
                sampleLabel: 'Aperçu d\'un devis avec lignes et partage.',
              }}
            >
              <DevisPage />
            </LicenseGate>,
          )}
        />
        <Route
          path="tools/cra"
          element={withSuspense(
            <LicenseGate
              toolName="Calendrier"
              description="Suivez vos jours travaillés."
              preview={{
                eyebrow: 'Aperçu calendrier', title: 'Calendrier',
                intro: 'Voyez comment les jours travaillent.',
                demo: <CraPreviewDemo />,
                items: [
                  { label: 'Mois visibles', value: '1', note: 'Free aperçu.' },
                  { label: 'Mois starter', value: '3', note: 'Usage étendu.' },
                  { label: 'Mois pro', value: '∞', note: 'Complet.' },
                  { label: 'Impression', value: 'Oui', note: 'Vue client.' },
                ],
                sampleLabel: 'Calendrier interactif — cliquez sur les jours.',
              }}
            >
              <CraPage />
            </LicenseGate>,
          )}
        />
        <Route
          path="tools/tresorerie"
          element={withSuspense(
            <LicenseGate
              toolName="Argent"
              description="Pilotez votre trésorerie."
              preview={{
                eyebrow: 'Aperçu argent', title: 'Argent',
                intro: 'Entrées, sorties, scénarios.',
                demo: <TresoreriePreviewDemo />,
                items: [
                  { label: 'Scénarios', value: '3', note: 'Prudent, normal, actif.' },
                  { label: 'Charges', value: 'Oui', note: 'Fixes et variables.' },
                  { label: 'Point mort', value: 'Oui', note: 'Repère simple.' },
                  { label: 'Alertes', value: 'Oui', note: 'Seuils.' },
                ],
                sampleLabel: 'Simulez votre réserve selon 3 scénarios.',
              }}
            >
              <TreasuryPage />
            </LicenseGate>,
          )}
        />
        <Route path="tools/activation" element={withSuspense(<ActivationPage />)} />
        <Route
          path="tools/estimation"
          element={withSuspense(
            <LicenseGate toolName="Chiffrage détaillé" description="Estimez le temps et le coût par fonctionnalité avant de proposer.">
              <EstimationPage />
            </LicenseGate>,
          )}
        />
        <Route
          path="tools/notes"
          element={withSuspense(
            <LicenseGate toolName="Notes" description="Notes cross-missions taguées et recherche plein texte.">
              <NotesPage />
            </LicenseGate>,
          )}
        />
        <Route
          path="tools/projections"
          element={withSuspense(
            <LicenseGate toolName="Projections" description="Projections CA 12 mois · 3 scénarios · seuils TVA / IS." requiredTier="pro">
              <ProjectionsPage />
            </LicenseGate>,
          )}
        />
        <Route
          path="tools/runway"
          element={withSuspense(
            <LicenseGate
              toolName="Réserve"
              description="Projetez votre activité."
              preview={{
                eyebrow: 'Aperçu réserve', title: 'Réserve',
                intro: 'Scénarios de durée.',
                demo: <RunwayPreviewDemo />,
                items: [
                  { label: 'Cas prudent', value: 'Oui', note: 'Tension.' },
                  { label: 'Cas normal', value: 'Oui', note: 'Base.' },
                  { label: 'Cas actif', value: 'Oui', note: 'Optimiste.' },
                  { label: 'Temps restant', value: 'Oui', note: 'Lecture immédiate.' },
                ],
                sampleLabel: 'Ajustez TJM et réserve pour voir votre runway.',
              }}
            >
              <RunwayPage />
            </LicenseGate>,
          )}
        />
        <Route path="tools/calc-mission" element={withSuspense(<CalcMissionPage />)} />
        <Route path="tools/calc-mission/goal" element={withSuspense(<GoalPage />)} />
        <Route path="tools/calc-mission/project" element={withSuspense(<ProjectPage />)} />
        <Route path="tools/calc-mission/comparator" element={<Navigate to="/tools/fiscal" replace />} />
        <Route
          path="tools/fiscal"
          element={withSuspense(
            <LicenseGate toolName="Optimisation fiscale" description="Leviers d'optimisation selon votre statut juridique." requiredTier="pro">
              <FiscalPage />
            </LicenseGate>,
          )}
        />
        <Route
          path="tools/benchmark"
          element={withSuspense(
            <LicenseGate toolName="Benchmark TJM" description="Comparez votre tarif au marché freelance France." requiredTier="pro">
              <BenchmarkPage />
            </LicenseGate>,
          )}
        />
        <Route
          path="tools/reporting"
          element={withSuspense(
            <LicenseGate toolName="Reporting" description="KPIs, funnel pipeline, CA mensuel et top clients." requiredTier="pro">
              <ReportingPage />
            </LicenseGate>,
          )}
        />
        <Route
          path="tools/scratchpad"
          element={withSuspense(
            <LicenseGate toolName="Éditeur de formules" description="Calculs libres avec variables nommées.">
              <ScratchpadPage />
            </LicenseGate>,
          )}
        />
        <Route
          path="tools/missio"
          element={withSuspense(
            <LicenseGate
              toolName="Missio"
              description="Suivez vos opportunités, missions et paiements."
              preview={{
                eyebrow: 'Aperçu mission', title: 'Missio',
                intro: 'Un cockpit lisible pour passer de l’opportunité à la mission, puis au paiement.',
                demo: <MissioPreviewDemo />,
                items: [
                  { label: 'Opportunités', value: '3', note: 'Repérez ce qui doit avancer.' },
                  { label: 'Missions', value: '3', note: 'Gardez chaque dossier au bon stade.' },
                  { label: 'Paiements', value: 'Suivi', note: 'De l’émission à l’encaissement.' },
                ],
                sampleLabel: 'Démo du cockpit.',
              }}
            >
              <MissioPage />
            </LicenseGate>,
          )}
        />
        <Route
          path="tools/mission-memory"
          element={withSuspense(
            <LicenseGate toolName="Retours missions" description="Carnet de bord des missions passées.">
              <MissionMemoryPage />
            </LicenseGate>,
          )}
        />
        <Route
          path="tools/proposal"
          element={withSuspense(
            <LicenseGate toolName="Proposition" description="Générateur de propositions de mission structurées.">
              <ProposalPage />
            </LicenseGate>,
          )}
        />
        <Route path="tools/tjm-radar" element={withSuspense(<TjmRadarPage />)} />
        <Route
          path="tools/chrono"
          element={withSuspense(
            <LicenseGate toolName="Chrono" description="Journal de facturation — suivez vos jours par mission et partagez un récapitulatif mensuel.">
              <ChronoPage />
            </LicenseGate>,
          )}
        />
        <Route path="tools/client-risk" element={withSuspense(<ClientRiskPage />)} />
        <Route path="tools/stack-registry" element={withSuspense(<StackRegistryPage />)} />
        <Route path="admin/licenses" element={<OwnerOnlyAdminRoute />} />
        <Route path="tools/admin/licenses" element={<OwnerOnlyAdminRoute />} />
        <Route path="admin/usage" element={<OwnerOnlyUsageRoute />} />
        <Route path="tools/admin/usage" element={<OwnerOnlyUsageRoute />} />
      </Route>
    </Routes>
  );
}
